import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Settings, Wrench, LineChart } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { IMAGES, STATS, HOME_SERVICES, CONTACT } from "../mock";
import { api } from "../lib/api";

const iconMap = { revisao: Settings, preparacao: Wrench, software: LineChart };

const Home = () => {
  const { t, lang } = useLanguage();
  const { content, cval } = useContent();
  const [gallery, setGallery] = useState([]);

  useEffect(() => {
    api.listGallery().then((g) => setGallery(g.slice(0, 3))).catch(() => {});
  }, []);

  const bannerSrc = content.banner_image || IMAGES.banner;
  const stats = [
    { value: content.stat_years || "25+", key: "stat_years" },
    { value: content.stat_tuned || "1000+", key: "stat_tuned" },
    { value: content.stat_dedication || "100%", key: "stat_dedication" },
  ];
  const gTitle = (g) => (lang === "en" && g.title_en ? g.title_en : g.title_pt);

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-screen flex flex-col items-center justify-center pt-28 pb-16 px-6 overflow-hidden bg-[#050711]">
        <div className="w-full max-w-6xl bf-section-fade">
          <img src={bannerSrc} alt="Barbo Factory" className="w-full object-contain rounded-2xl" />
        </div>
        <div className="relative z-10 text-center max-w-3xl mt-8 bf-section-fade">
          <p className="text-xl md:text-2xl text-white font-semibold mb-3">{cval("hero_subtitle", lang, t("hero_subtitle"))}</p>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8 leading-relaxed">{cval("hero_desc", lang, t("hero_desc"))}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" className="bf-btn-primary text-white font-semibold px-8 py-4 rounded-full flex items-center justify-center gap-2 uppercase tracking-wide text-sm">
              {t("cta_book")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/about" className="bf-btn-ghost font-semibold px-8 py-4 rounded-full flex items-center justify-center uppercase tracking-wide text-sm">
              {t("cta_history")}
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-6 mt-14 max-w-2xl mx-auto">
            {stats.map((s) => (
              <div key={s.key}>
                <div className="text-3xl md:text-5xl font-extrabold bf-cyan-text">{s.value}</div>
                <div className="text-gray-400 text-xs md:text-sm mt-2">{t(s.key)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History */}
      <section className="py-24 px-6 bg-[#0a1024]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="relative rounded-2xl overflow-hidden bf-card-hover border border-white/10">
            <img src={IMAGES.workshop} alt="Barbo Factory Workshop" className="w-full h-[380px] object-cover" />
          </div>
          <div>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-6">{t("home_history_title")}</h2>
            <p className="text-gray-400 leading-relaxed mb-4">{t("home_history_p1")}</p>
            <p className="text-gray-400 leading-relaxed mb-8">{t("home_history_p2")}</p>
            <Link to="/about" className="bf-btn-ghost font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm">
              {t("btn_history")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">{t("services_title")}</h2>
            <p className="text-gray-400">{t("services_subtitle")}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {HOME_SERVICES.map((s) => {
              const Icon = iconMap[s.id] || Settings;
              return (
                <div key={s.id} className="bf-glass bf-card-hover rounded-2xl p-8">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#22d3ee]/20 to-[#22d3ee]/5 border border-[#22d3ee]/30 flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#22d3ee]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{t(s.titleKey)}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-5">{t(s.descKey)}</p>
                  <div className="flex flex-wrap gap-2">
                    {s.tags.map((tag) => (
                      <span key={tag} className="text-xs px-3 py-1 rounded-full bg-white/5 text-[#67e8f9] border border-[#22d3ee]/20">{tag}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" className="bf-btn-ghost font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm">
              {t("btn_services")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery preview */}
      <section className="py-24 px-6 bg-[#0a1024]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3">{t("gallery_title")}</h2>
            <p className="text-gray-400">{t("gallery_subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {gallery.map((g) => (
              <div key={g.id} className="relative rounded-2xl overflow-hidden group border border-white/10">
                <img src={g.image} alt="Gallery preview" className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070b18] via-transparent to-transparent opacity-70" />
                <span className="absolute bottom-4 left-4 text-white font-semibold">{gTitle(g)}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/gallery" className="bf-btn-ghost font-semibold px-6 py-3 rounded-full inline-flex items-center gap-2 text-sm">
              {t("btn_gallery")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-28 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0d1428] to-[#131b32]" />
        <div className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">{t("booking_cta_title")}</h2>
          <p className="text-gray-400 mb-10">{t("booking_cta_subtitle")}</p>
          <Link to="/booking" className="bf-btn-primary text-white font-semibold px-10 py-4 rounded-full inline-flex items-center gap-2 uppercase tracking-wide text-sm">
            {t("cta_book")} <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-gray-500 text-sm mt-8">{CONTACT.phone} · {CONTACT.email}</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
